import React from 'react';
import { Link } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { getLaunchReturnOrigin } from '../../lib/oauthRelay';
import { routes } from '../../lib/routes';

/**
 * V1.5.M10 B-P2 — canonical-origin Google launch interstitial.
 *
 * Reached only on app.2connect.ai, when a white-label subdomain bounced its
 * "Continue with Google" here (Google has no wildcard JS origins, so the popup
 * must run on the one allowlisted origin). The page is themed for the CLIENT
 * via the invite branding, so although the URL bar reads app.2connect.ai the
 * card shows the client's logo + name. One click runs the Google popup; the
 * AuthContext onSuccess then mints a relay code and sends the session back to
 * the originating subdomain.
 */
const OAuthLaunchPage: React.FC = () => {
  const { googleLogin } = useAuth();
  const { logoDark, name } = useBranding();
  const returnOrigin = getLaunchReturnOrigin();

  // Open-redirect guard: no valid 2connect.ai return origin = don't proceed.
  if (!returnOrigin) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center px-6 text-center'>
        <h1 className='text-2xl font-heading font-medium text-secondary-500 mb-2'>
          This sign-in link looks invalid
        </h1>
        <p className='text-body-500 mb-6 max-w-md'>
          We couldn't verify where to send you after signing in. Please return
          to your event link and try again.
        </p>
        <Link to={routes.login} className='text-primary-500 font-semibold'>
          Go to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col items-center justify-center px-6'>
      <div className='w-full max-w-[26rem] rounded-[1.5rem] border border-gray-200 p-10 flex flex-col items-center text-center'>
        <img
          src={logoDark}
          alt={name}
          className='h-12 w-auto mb-8 object-contain'
        />
        <h1 className='text-2xl font-heading font-medium text-secondary-500 mb-2'>
          Sign in to {name}
        </h1>
        <p className='text-body-500 mb-8'>
          Continue with your Google account to access {name}.
        </p>
        <button
          onClick={() => googleLogin()}
          className='w-full flex items-center justify-center gap-3 border border-gray-300 rounded-full py-3 font-medium hover:bg-gray-50 transition-colors'
        >
          <FcGoogle size={22} /> Continue with Google
        </button>
        <p className='text-xs text-gray-400 mt-6'>
          You'll return to your event automatically.
        </p>
      </div>
    </div>
  );
};

export default OAuthLaunchPage;
